/**
 * Events Page
 * List and manage events
 */

import React, { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEvents,
  cancelEvent,
  softDeleteEvent,
  bulkUpdateEventStatus,
  bulkDeleteEvents,
  describeDeleteError,
  getEventPromoCodes,
  getEventModifyRights,
} from "../services/events.service";
import { formatAddress } from "../utils/formatters";
import {
  formatEventTimeWithZone,
  formatEventDateRange,
  CENTRAL_TZ,
} from "../utils/eventTime";
import { eventKeys } from "../hooks/useEvents";
import { EventDailyHoursList } from "../components/events/EventDailyHours";
import { useEventDayHours } from "../hooks/useEventDayHours";
import { resolveDailyHours } from "../utils/eventDayHours";

/** Per-viewer display preference for the Events list time zone. */
const EVENTS_CENTRAL_TZ_KEY = "events.showTimesInCentral";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import {
  TrashIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useToast } from "../components/ui/Toast";
import { useAuth } from "../contexts/AuthContext";
import { showsSetupIncomplete } from "../utils/eventTypePolicy";
import { isFeatureEnabled } from "../utils/featureFlags";
import {
  canMarkEventCompleted,
  completionRefusalMessage,
} from "../utils/eventCompletePermission";
import CancellationReasonModal from "../components/events/CancellationReasonModal";
import EventStatusBadge from "../components/events/EventStatusBadge";
// Smart Admin replacement R1 (2026-05-17) — use the shared full-vocabulary
// constant so list filter labels stay in sync with create/edit + so
// deprecated event values still render correctly. Audit must-fix:
// previously this file declared a divergent 5-value local list.
// R9 (2026-05-25): EVENT_STATUSES added so the Status select stays in
// sync with the constant (previously hardcoded 5 of 6 values, missing
// "postponed") and so the URL-param whitelist derives from the same
// source of truth.
import { EVENT_STATUSES, EVENT_TYPES } from "../utils/constants";
// R4 (2026-05-17): US state list for the state filter dropdown.
// 2026-06-07-post-deploy: switched to code/name pairs so the
// filter sends 2-letter abbrevs (matches DB), not full names.
import { US_STATE_OPTIONS } from "../types/list.types";
import ExportButton from "../components/common/ExportButton";
import EventsOrgFilter from "../components/events/EventsOrgFilter";
import EventsRepFilter from "../components/events/EventsRepFilter";
import {
  LEAD_EVENT_REP_POSITION_TYPES,
  TERRITORY_MANAGER_POSITION_TYPES,
  REGIONAL_MANAGER_POSITION_TYPES,
  SALES_DIRECTOR_POSITION_TYPES,
} from "../utils/salesPositionDisplay";
import { EVENTS_EXPORT_COLUMNS } from "../components/events/eventsExport";
import { buildEventsExportRows } from "../components/events/buildEventsExportRows";
import SearchBar from "../components/common/SearchBar";
import EventsAwaitingSourceCTA from "../components/events/EventsAwaitingSourceCTA";

// Server-side page size for the events list. Matches the existing
// limit hardcoded in getEvents above; centralised so the pagination
// footer math stays in sync.
const PAGE_SIZE = 50;

// R9 (2026-05-25): URL-param whitelists for filter values. Wrapping a
// set around the constant arrays so a malformed `?status=DROP+TABLE`
// param falls back to safe defaults instead of flowing through to the
// SQL caller. Sort/dir are server-side allow-listed too but defended
// in depth here so the React Query key doesn't mutate on garbage input.
// "active" is a synthetic default view (hide completed + cancelled); "all"
// shows every status; the rest are the real events.status values.
const SAFE_STATUS: ReadonlySet<string> = new Set([
  "active",
  "all",
  ...EVENT_STATUSES.map((s) => s.value),
]);
// Statuses hidden from the default "active" view. Only Cancelled is hidden;
// Completed events remain visible in the default view.
const HIDDEN_STATUSES = ["cancelled"];
const SAFE_TYPE: ReadonlySet<string> = new Set([
  "all",
  ...EVENT_TYPES.map((t) => t.value),
]);
// Setup-completeness filter: all / only-incomplete / only-complete.
const SAFE_SETUP: ReadonlySet<string> = new Set([
  "all",
  "incomplete",
  "complete",
]);
// mig 1279 (owner 2026-10-30): Gun Show - Not Advertised filter: all / only not advertised / only advertised.
const SAFE_ADV: ReadonlySet<string> = new Set([
  "all",
  "not_advertised",
  "advertised",
]);
const NOT_ADVERTISED_TITLE =
  "Gun Show - Not Advertised: no Event in a Box invitations and no landing page; still listed publicly; left out of close-rate and Sales by Type reports";
const SAFE_SORT: ReadonlySet<string> = new Set([
  "start_time",
  "created_at",
  "title",
  "status",
  "type",
  "setup_incomplete",
]);
const SAFE_DIR: ReadonlySet<string> = new Set(["asc", "desc"]);
const ISO_DATE_RX = /^\d{4}-\d{2}-\d{2}$/;

const Events: React.FC = () => {
  const navigate = useNavigate();
  // MSRs get a purpose-built, server-filtered event list in their own portal (/msr) — keep them out
  // of the all-events admin page and route them to their (only-their-events) schedule.
  const { user } = useAuth();
  const isMsr = (user?.role ?? "").toLowerCase().replace(/_/g, "") === "msr";
  // Events CSV import: the /events/create roles (the database enforces the same set, migration 980).
  const canImportEvents = ["superadmin", "admin", "staff", "manager"].includes(
    (user?.role ?? "").toLowerCase().replace(/_/g, ""),
  );
  useEffect(() => {
    if (isMsr) {
      navigate("/msr", { replace: true });
    }
  }, [isMsr, navigate]);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  // R9 (2026-05-25): filter + page + sort state lives in the URL so
  // back-nav from EventDetail restores the operator's exact list view.
  // Deleted: per-field useState + a useEffect that reset page on any
  // filter change. Page reset is folded into updateParams so deep links
  // like /events?status=scheduled&page=2 don't get wiped on mount.
  const [searchParams, setSearchParams] = useSearchParams();

  const safeRead = (
    key: string,
    fallback: string,
    whitelist?: ReadonlySet<string>,
  ): string => {
    const v = searchParams.get(key);
    if (v === null) {
      return fallback;
    }
    if (whitelist && !whitelist.has(v)) {
      return fallback;
    }
    return v;
  };

  const rawPage = parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;
  // Default to the "active" view — completed/cancelled events are hidden unless
  // the operator explicitly picks a status or "All statuses". Absent URL key =
  // "active"; selecting "active" drops the key (see setStatusFilter).
  const statusFilter = safeRead("status", "active", SAFE_STATUS);
  const typeFilter = safeRead("type", "all", SAFE_TYPE);
  const setupFilter = safeRead("setup", "all", SAFE_SETUP);
  const advFilter = safeRead("adv", "all", SAFE_ADV);
  const notAdvertisedParam =
    advFilter === "not_advertised"
      ? true
      : advFilter === "advertised"
        ? false
        : undefined;
  // Translate the setup filter into the getEvents `setupIncomplete` param.
  const setupIncompleteParam =
    setupFilter === "incomplete"
      ? true
      : setupFilter === "complete"
        ? false
        : undefined;
  const stateFilter = safeRead("state", "all");
  const organizationFilter = searchParams.get("orgId") ?? "";
  // OI-10 (2026-06-20): Lead/Event Rep + TM/RM/SD position-id filters (URL state).
  const repFilter = searchParams.get("repId") ?? "";
  const tmFilter = searchParams.get("tmId") ?? "";
  const rmFilter = searchParams.get("rmId") ?? "";
  const sdFilter = searchParams.get("sdId") ?? "";
  const dateFrom = (() => {
    const v = searchParams.get("from") ?? "";
    return ISO_DATE_RX.test(v) ? v : "";
  })();
  const dateTo = (() => {
    const v = searchParams.get("to") ?? "";
    return ISO_DATE_RX.test(v) ? v : "";
  })();
  const searchTerm = searchParams.get("q") ?? "";
  // Set by EventsCalendar when it deep-links into this list. calDate/calView
  // carry the calendar's own position so "Back to calendar" lands on the month
  // or week the operator left. Validated the same way every other URL param
  // here is — an unexpected value simply means no back button.
  const returnToCalendar = (() => {
    if (searchParams.get("ret") !== "cal") {
      return null;
    }
    const calDate = searchParams.get("calDate") ?? "";
    const calView = searchParams.get("calView") ?? "";
    const p = new URLSearchParams();
    if (ISO_DATE_RX.test(calDate)) {
      p.set("calDate", calDate);
    }
    if (calView === "month" || calView === "week") {
      p.set("calView", calView);
    }
    // Carry the filters back too. The calendar sent us here filtered; returning to an
    // unfiltered calendar would silently change what the operator is looking at.
    // Validated against the same whitelists this page already uses.
    const backStatus = searchParams.get("status");
    if (backStatus && SAFE_STATUS.has(backStatus) && backStatus !== "active") {
      p.set("status", backStatus);
    }
    const backSetup = searchParams.get("setup");
    if (backSetup && SAFE_SETUP.has(backSetup) && backSetup !== "all") {
      p.set("setup", backSetup);
    }
    const backState = searchParams.get("state");
    if (backState && backState !== "all") {
      p.set("state", backState);
    }
    const qs = p.toString();
    return qs ? `/events/calendar?${qs}` : "/events/calendar";
  })();
  const sortField = safeRead("sort", "start_time", SAFE_SORT);
  const sortDirection = safeRead("dir", "desc", SAFE_DIR) as "asc" | "desc";

  // Merge a patch into the URL search params. Empty / null / "all"
  // values drop the key so the URL stays clean. resetPage defaults to
  // true because every filter change should drop back to page 1;
  // pagination handlers pass false to preserve the page param.
  const updateParams = (
    patch: Record<string, string | null>,
    resetPage: boolean = true,
  ) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || v === "all") {
            next.delete(k);
          } else {
            next.set(k, v);
          }
        }
        if (resetPage) {
          next.delete("page");
        }
        return next;
      },
      { replace: true },
    );
  };

  // Status has its own setter because the shared updateParams treats "all" as a
  // droppable default — but here the droppable default is "active", and "all"
  // must persist explicitly in the URL.
  const setStatusFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "active") {
          next.delete("status"); // default view
        } else {
          next.set("status", value); // "all" or a specific status
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  // Translate the status filter into getEvents params. Shared by the list query
  // and the export so "what you see is what you get".
  const statusQueryParams = (
    value: string,
  ): { status?: string; excludeStatuses?: string[] } => {
    if (value === "active") {
      return { excludeStatuses: HIDDEN_STATUSES };
    }
    if (value === "all") {
      return {};
    }
    return { status: value };
  };

  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [cancellingEventId, setCancellingEventId] = useState<string | null>(
    null,
  );
  const [cancellingEventTitle, setCancellingEventTitle] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  // Display-only preference: render every row's time in Central rather than in
  // each event's own zone. It is not a filter — it changes no query — so it
  // lives in localStorage instead of the URL params that drive the fetch, and
  // it persists across visits for the operator who works in one zone.
  const [showCentral, setShowCentral] = useState<boolean>(() => {
    try {
      return localStorage.getItem(EVENTS_CENTRAL_TZ_KEY) === "1";
    } catch {
      // A browser blocking site data must not break the list.
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(EVENTS_CENTRAL_TZ_KEY, showCentral ? "1" : "0");
    } catch {
      // Ignore — the toggle still works for this session.
    }
  }, [showCentral]);
  // undefined = leave each event in its own zone (the default).
  const displayTz = showCentral ? CENTRAL_TZ : undefined;

  // Fetch events - uses eventKeys.lists() prefix so invalidation from useUpdateEvent works
  const { data, isLoading, error } = useQuery({
    queryKey: [
      ...eventKeys.lists(),
      {
        page,
        status: statusFilter,
        type: typeFilter,
        setup: setupFilter,
        adv: advFilter,
        state: stateFilter,
        organization_id: organizationFilter,
        repId: repFilter,
        tmId: tmFilter,
        rmId: rmFilter,
        sdId: sdFilter,
        dateFrom,
        dateTo,
        searchTerm,
        sortField,
        sortDirection,
      },
    ],
    queryFn: () =>
      getEvents({
        page,
        limit: PAGE_SIZE,
        ...statusQueryParams(statusFilter),
        setupIncomplete: setupIncompleteParam,
        notAdvertised: notAdvertisedParam,
        type: typeFilter === "all" ? undefined : typeFilter,
        state: stateFilter === "all" ? undefined : stateFilter,
        organization_id: organizationFilter || undefined,
        sales_position_id: repFilter || undefined,
        territory_manager_position_id: tmFilter || undefined,
        regional_manager_position_id: rmFilter || undefined,
        sales_director_position_id: sdFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: searchTerm || undefined,
        sortField,
        sortDirection,
      }),
  });

  // R9 (2026-05-25): the prior reset-page useEffect that ran on every
  // filter/sort change has been deleted — it fired on mount and would
  // clobber URL-deep-linked `?page=N`. Page reset is now inlined into
  // every filter-change handler via updateParams(..., resetPage=true).
  // Sort handler (toggleSort) does the same.

  // Clear bulk selection on page change — selected events from page N
  // aren't visible on page N+1, so a bulk action button counting them
  // is confusing.
  useEffect(() => {
    setSelectedEvents(new Set());
  }, [page]);

  // Soft delete event mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => softDeleteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      showToast("success", "Event deleted");
    },
    onError: (error: Error) => {
      showToast(
        "error",
        `Couldn't delete this event: ${describeDeleteError(error)}`,
      );
    },
  });

  const handleCancel = (id: string, title?: string) => {
    setCancellingEventId(id);
    setCancellingEventTitle(title || "");
  };

  const handleCancellationConfirm = async (reason: string, notes: string) => {
    if (!cancellingEventId) {
      return;
    }
    setIsCancelling(true);
    try {
      await cancelEvent(cancellingEventId, reason, notes);
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      showToast("success", "Event cancelled");
      setCancellingEventId(null);
    } catch (error: any) {
      showToast("error", `Failed to cancel event: ${error.message}`);
    } finally {
      setIsCancelling(false);
    }
  };

  // Promo code per listed event (agents prep for the day from this list). Declared ABOVE the
  // loading/error early returns so hook order never changes between renders.
  const pageEventIds = ((data?.events || []) as Array<{ id: string }>).map(
    (e) => e.id,
  );
  const { data: promoCodes } = useQuery({
    queryKey: ["event-promo-codes", pageEventIds.join(",")],
    queryFn: () => getEventPromoCodes(pageEventIds),
    enabled: pageEventIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Which listed events this user can change (2026-10-01). Cancel, Delete and selection (for the
  // bulk actions) are offered only there, so a manager never sees a button the database will refuse.
  // Until it loads, nothing is offered (fails closed).
  const { data: modifyRights } = useQuery({
    queryKey: ["event-modify-rights", user?.id, pageEventIds.join(",")],
    queryFn: () => getEventModifyRights(pageEventIds),
    enabled: pageEventIds.length > 0,
    staleTime: 60 * 1000,
  });
  const canModify = (id: string): boolean => modifyRights?.[id] === true;
  const selectableCount = pageEventIds.filter(canModify).length;

  const handleDelete = async (id: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this event? It will be hidden from the list but can be recovered by an admin.",
      )
    ) {
      return;
    }
    // mutate, not mutateAsync: onError already shows the toast, and a refusal (e.g. EVENT_HAS_ACTIVITY,
    // migration 401) is an expected outcome, not an unhandled rejection.
    deleteMutation.mutate(id);
  };

  // Hours each day of multi-day events on this page (migration 2026-10-27-1120); one request per page.
  const { data: dayHoursMap } = useEventDayHours(data?.events || []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading events..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card
        variant="outlined"
        padding="md"
        className="border-red-200 bg-red-50"
      >
        <p className="text-red-800">Error loading events. Please try again.</p>
      </Card>
    );
  }

  // R4 (2026-05-17): all filters are server-side. The "filteredEvents"
  // variable name is preserved to minimize diff against the rendering
  // logic below.
  const events = data?.events || [];
  const filteredEvents = events;

  // Toggle single event selection
  const toggleEventSelection = (eventId: string) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(eventId)) {
      newSelected.delete(eventId);
    } else {
      newSelected.add(eventId);
    }
    setSelectedEvents(newSelected);
  };

  // Toggle all events selection
  const toggleSelectAll = () => {
    const selectable = filteredEvents
      .map((e: any) => e.id as string)
      .filter(canModify);
    if (selectedEvents.size === selectable.length && selectable.length > 0) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(selectable));
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedEvents.size === 0) {
      return;
    }

    setIsBulkActionLoading(true);
    try {
      await bulkUpdateEventStatus(Array.from(selectedEvents), newStatus);
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      showToast(
        "success",
        `${selectedEvents.size} event(s) updated to ${newStatus}`,
      );
      setSelectedEvents(new Set());
    } catch (error) {
      console.error("Bulk status update failed:", error);
      showToast(
        "error",
        completionRefusalMessage(error) ?? "Failed to update events",
      );
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  // Group the reasons rather than listing 19 of them; the operator needs to know WHAT is blocking,
  // not which row said so first.
  const summariseFailures = (failed: { id: string; reason: string }[]) => {
    const byReason = new Map<string, number>();
    for (const f of failed) {
      byReason.set(f.reason, (byReason.get(f.reason) ?? 0) + 1);
    }
    return Array.from(byReason.entries())
      .map(([reason, n]) => (n > 1 ? `${n} × ${reason}` : reason))
      .join("; ");
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedEvents.size === 0) {
      return;
    }
    if (
      !confirm(
        `Delete ${selectedEvents.size} event(s)? They will be removed from the events list.`,
      )
    ) {
      return;
    }

    setIsBulkActionLoading(true);
    try {
      const { deleted, failed } = await bulkDeleteEvents(
        Array.from(selectedEvents),
      );
      queryClient.invalidateQueries({ queryKey: eventKeys.all });

      // Keep whatever could not be deleted selected, so the next action operates on exactly the
      // events that still need attention rather than making the user re-find them.
      setSelectedEvents(new Set(failed.map((f) => f.id)));

      if (failed.length === 0) {
        showToast("success", `${deleted.length} event(s) deleted`);
      } else if (deleted.length === 0) {
        showToast("error", `Could not delete: ${summariseFailures(failed)}`);
      } else {
        showToast(
          "warning",
          `${deleted.length} deleted. ${failed.length} could not be: ${summariseFailures(failed)}`,
        );
      }
    } catch (error) {
      console.error("Bulk delete failed:", error);
      showToast(
        "error",
        error instanceof Error ? error.message : "Failed to delete events",
      );
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedEvents(new Set());
  };

  // Toggle sort on a column. resetPage=true (default) so changing sort
  // drops back to page 1.
  const toggleSort = (field: string) => {
    const nextDir =
      sortField === field ? (sortDirection === "asc" ? "desc" : "asc") : "desc";
    updateParams({ sort: field, dir: nextDir });
  };

  // Sort indicator arrow
  const SortArrow = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return null;
    }
    return (
      <span className="ml-1 text-gray-400">
        {sortDirection === "asc" ? "\u2191" : "\u2193"}
      </span>
    );
  };

  // Get location display for an event
  const getLocationDisplay = (event: any) => {
    if (event.location_name) {
      return <span>{event.location_name}</span>;
    }
    if (
      typeof event.location_address === "object" &&
      event.location_address?.city &&
      event.location_address?.state
    ) {
      return (
        <span>{`${event.location_address.city}, ${event.location_address.state}`}</span>
      );
    }
    if (event.location_address) {
      return <span>{formatAddress(event.location_address)}</span>;
    }
    // Physical event with no location = red warning
    if (event.location_type === "physical") {
      return (
        <span className="inline-flex items-center gap-1 text-red-600">
          <ExclamationTriangleIcon className="h-4 w-4" />
          Missing location
        </span>
      );
    }
    return <span className="text-gray-400">No location</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Events
          </h1>
          <p className="text-gray-600 mt-1">Manage your events and seminars</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Back to the calendar the operator came from, restoring its exact
              month/week rather than resetting the view. Only rendered when the
              calendar actually sent them here. */}
          {returnToCalendar && (
            <Button
              variant="secondary"
              onClick={() => navigate(returnToCalendar)}
            >
              ← Back to calendar
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => navigate("/events/calendar")}
          >
            Calendar view
          </Button>
          {isFeatureEnabled("eventCsvImport") && canImportEvents && (
            <Button
              variant="secondary"
              onClick={() => navigate("/events/import")}
            >
              Import CSV
            </Button>
          )}
          <Button onClick={() => navigate("/events/create")}>
            Create Event
          </Button>
        </div>
      </div>

      {/* mig 1360: gun shows / range nights whose event CTA is waiting for a promoter / host CTA */}
      <EventsAwaitingSourceCTA />

      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search — explicit submit (Enter / Search button), no live querying */}
        <div className="w-full sm:flex-1 sm:min-w-[260px]">
          <SearchBar
            placeholder="Search by name, type, or location..."
            initialValue={searchTerm}
            onSearch={(q) => updateParams({ q })}
          />
        </div>
        {/* Type Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Type:</label>
          <select
            value={typeFilter}
            onChange={(e) => updateParams({ type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Types</option>
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        {/* Status Filter. R9 (2026-05-25): mapped over EVENT_STATUSES so
            the dropdown stays in sync with the constant (was missing
            "postponed" previously). */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="active">Active (hide cancelled)</option>
            <option value="all">All statuses</option>
            {EVENT_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        {/* Setup-completeness filter — events saved from the wizard as "finish
            later" are badged Incomplete. */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Setup:</label>
          <select
            value={setupFilter}
            onChange={(e) => updateParams({ setup: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All setup</option>
            <option value="incomplete">Incomplete only</option>
            <option value="complete">Complete only</option>
          </select>
        </div>
        {/* mig 1279: Gun Show - Not Advertised */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">
            Advertising:
          </label>
          <select
            value={advFilter}
            onChange={(e) => updateParams({ adv: e.target.value })}
            data-testid="events-adv-filter"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All events</option>
            <option value="not_advertised">Not advertised only</option>
            <option value="advertised">Advertised only</option>
          </select>
        </div>
        {/* R4 (2026-05-17): State Filter — uses events.location_state */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">State:</label>
          <select
            value={stateFilter}
            onChange={(e) => updateParams({ state: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All States</option>
            {US_STATE_OPTIONS.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        {/* R4 (2026-05-17): Organization Filter — match-any over
            event_organizations per audit Decision 3. Typeahead reuses
            the R2A identifier-aware search pattern.
            R9 (2026-05-25): id-only URL state; component re-fetches the
            display label so renames don't go stale. */}
        <EventsOrgFilter
          value={organizationFilter}
          onChange={(id) => updateParams({ orgId: id || null })}
        />
        {/* OI-10 (2026-06-20): Lead/Event Rep + TM/RM/SD typeahead filters.
            Tier→position_type mapping mirrors the event-create Sales Hierarchy
            section so the choices match. All filter server-side. */}
        <EventsRepFilter
          label="Rep"
          value={repFilter}
          onChange={(id) => updateParams({ repId: id || null })}
          allowedTypes={LEAD_EVENT_REP_POSITION_TYPES}
        />
        <EventsRepFilter
          label="TM"
          value={tmFilter}
          onChange={(id) => updateParams({ tmId: id || null })}
          allowedTypes={TERRITORY_MANAGER_POSITION_TYPES}
        />
        <EventsRepFilter
          label="RM"
          value={rmFilter}
          onChange={(id) => updateParams({ rmId: id || null })}
          allowedTypes={REGIONAL_MANAGER_POSITION_TYPES}
        />
        <EventsRepFilter
          label="SD"
          value={sdFilter}
          onChange={(id) => updateParams({ sdId: id || null })}
          allowedTypes={SALES_DIRECTOR_POSITION_TYPES}
        />
        {/* Date Range Filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => updateParams({ from: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => updateParams({ to: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        {/* Time-zone display toggle. Purely presentational: every row shows
            its own zone label by default ("3:00 PM EDT"), and this converts
            the whole list to Central so times across states can be compared
            at a glance. Both the date and the time are re-derived in the
            chosen zone — a 12:30 AM Eastern event is 11:30 PM Central on the
            PREVIOUS day, so converting the time alone would misdate it. */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showCentral}
            onChange={(e) => setShowCentral(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Show times in Central
          </span>
        </label>
        {/* Clear filters. Default status is now "active", so a non-default
            status (including "all") counts as an active filter worth clearing. */}
        {(statusFilter !== "active" ||
          typeFilter !== "all" ||
          setupFilter !== "all" ||
          stateFilter !== "all" ||
          organizationFilter ||
          repFilter ||
          tmFilter ||
          rmFilter ||
          sdFilter ||
          dateFrom ||
          dateTo ||
          searchTerm) && (
          <button
            onClick={() =>
              updateParams({
                status: null,
                type: null,
                setup: null,
                state: null,
                orgId: null,
                repId: null,
                tmId: null,
                rmId: null,
                sdId: null,
                from: null,
                to: null,
                q: null,
              })
            }
            className="text-sm text-primary-600 hover:text-primary-800"
          >
            Clear filters
          </button>
        )}
        {/* R4 Export — service-oriented ExportButton handles the
            paginated fetch + XLSX generation. Uses the SAME filter
            object as the list query, so what you see is what you get. */}
        <div className="ml-auto">
          <ExportButton
            filters={{
              ...statusQueryParams(statusFilter),
              setupIncomplete: setupIncompleteParam,
              notAdvertised: notAdvertisedParam,
              type: typeFilter === "all" ? undefined : typeFilter,
              state: stateFilter === "all" ? undefined : stateFilter,
              organization_id: organizationFilter || undefined,
              sales_position_id: repFilter || undefined,
              territory_manager_position_id: tmFilter || undefined,
              regional_manager_position_id: rmFilter || undefined,
              sales_director_position_id: sdFilter || undefined,
              dateFrom: dateFrom || undefined,
              dateTo: dateTo || undefined,
              search: searchTerm || undefined,
              sortField,
              sortDirection,
            }}
            fetchPage={async (filters, p, l) => {
              const res = await getEvents({ ...filters, page: p, limit: l });
              return { rows: res.events, total: res.total };
            }}
            // Hydration (stats, position names, promo codes) lives in buildEventsExportRows;
            // any failed lookup fails the export rather than shipping blank columns.
            mapRows={(rows) => buildEventsExportRows(rows)}
            columns={EVENTS_EXPORT_COLUMNS}
            filename="events"
            format="xlsx"
          />
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedEvents.size > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary-800">
              {selectedEvents.size} event{selectedEvents.size > 1 ? "s" : ""}{" "}
              selected
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1"
            >
              <XMarkIcon className="h-4 w-4" />
              Clear
            </button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Bulk Status Change */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Change status to:</label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkStatusChange(e.target.value);
                    e.target.value = "";
                  }
                }}
                disabled={isBulkActionLoading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select status...</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                {/* Completing ("closing") an event is admin/superadmin only (2026-09-30); DB-enforced too. */}
                {canMarkEventCompleted(user?.role) && (
                  <option value="completed">Completed</option>
                )}
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {/* Bulk Delete */}
            <Button
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={isBulkActionLoading}
              icon={TrashIcon}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Events list */}
      <Card>
        {/* Mobile: stacked cards (the desktop table below is hidden < md so the
            field team gets a reflowed, tappable list instead of a wide scroll). */}
        <div className="md:hidden divide-y divide-gray-200">
          {filteredEvents.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              {searchTerm
                ? `No events found matching "${searchTerm}"`
                : "No events found"}
            </div>
          ) : (
            filteredEvents.map((event: any) => {
              const backTo = encodeURIComponent(
                location.pathname + location.search,
              );
              return (
                <div
                  key={event.id}
                  onClick={() =>
                    navigate(`/events/${event.id}?backTo=${backTo}`)
                  }
                  className={`px-4 py-4 cursor-pointer ${selectedEvents.has(event.id) ? "bg-primary-50" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/events/${event.id}?backTo=${backTo}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm font-medium text-gray-900 break-words hover:underline"
                        >
                          {event.title}
                        </Link>
                        {event.is_recurring && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Recurring
                          </span>
                        )}
                        {event.cta_count > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {event.cta_count} CTA
                            {event.cta_count !== 1 ? "s" : ""}
                          </span>
                        )}
                        {event.comment_count > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                            title={`${event.comment_count} Comment${event.comment_count !== 1 ? "s" : ""}`}
                          >
                            {event.comment_count}
                            <ChatBubbleLeftRightIcon className="h-3 w-3" />
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {formatEventDateRange(
                          event.start_time,
                          event.end_time,
                          displayTz || event.timezone,
                        )}
                        {" · "}
                        {event.all_day
                          ? "All day"
                          : formatEventTimeWithZone(
                              event.start_time,
                              event.timezone,
                              displayTz,
                            )}
                        <EventDailyHoursList
                          days={resolveDailyHours(
                            event,
                            dayHoursMap?.get(event.id),
                          )}
                          compact
                          className="mt-0.5 text-gray-500"
                          note={
                            displayTz && displayTz !== event.timezone
                              ? "hours each day, event local time"
                              : undefined
                          }
                        />
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 truncate">
                        {getLocationDisplay(event)}
                        {(() => {
                          const st =
                            event.location_state ||
                            (typeof event.location_address === "object" &&
                              event.location_address?.state) ||
                            event.state ||
                            "";
                          // getLocationDisplay shows the venue NAME without a state only when
                          // location_name is set; every other branch already includes the state, so
                          // append it only in the name branch to avoid a duplicate ("NC · NC").
                          return event.location_name && st ? (
                            <span className="text-gray-400"> · {st}</span>
                          ) : null;
                        })()}
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                          {event.type || "N/A"}
                        </span>
                        <EventStatusBadge event={event} />
                        {promoCodes?.[event.id] && (
                          <span className="px-2 py-1 text-xs font-mono rounded bg-gray-100 text-gray-700">
                            Promo: {promoCodes[event.id]}
                          </span>
                        )}
                        {(event as { not_advertised?: boolean })
                          .not_advertised && (
                          <span
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700"
                            title={NOT_ADVERTISED_TITLE}
                            data-testid="not-advertised-badge"
                          >
                            Not advertised
                          </span>
                        )}
                        {showsSetupIncomplete(event) && (
                          <span
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800"
                            title="Setup not finished — saved from the create wizard to complete later."
                          >
                            Incomplete
                          </span>
                        )}
                      </div>
                    </div>
                    {canModify(event.id) && (
                      <input
                        type="checkbox"
                        checked={selectedEvents.has(event.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleEventSelection(event.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                      />
                    )}
                  </div>
                  {canModify(event.id) && (
                    <div className="mt-3 flex items-center gap-4">
                      <button
                        className="text-sm text-orange-600 hover:text-orange-900"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancel(event.id, event.title);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="text-sm text-red-600 hover:text-red-900"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(event.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Desktop: full table (hidden on phones) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  {selectableCount > 0 && (
                    <input
                      type="checkbox"
                      checked={selectedEvents.size === selectableCount}
                      onChange={toggleSelectAll}
                      title="Select every event on this page you can change"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    />
                  )}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                  onClick={() => toggleSort("title")}
                >
                  Event
                  <SortArrow field="title" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                  onClick={() => toggleSort("start_time")}
                >
                  Date
                  <SortArrow field="start_time" />
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                {/* R4 (2026-05-17): State column per Heather page 7 */}
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                <th className="px-3 py-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promo Code
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                  onClick={() => toggleSort("status")}
                >
                  Status
                  <SortArrow field="status" />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
                  onClick={() => toggleSort("setup_incomplete")}
                >
                  Setup
                  <SortArrow field="setup_incomplete" />
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEvents.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {searchTerm
                      ? `No events found matching "${searchTerm}"`
                      : "No events found"}
                  </td>
                </tr>
              ) : (
                filteredEvents.map((event: any) => (
                  <tr
                    key={event.id}
                    className={`hover:bg-gray-50 cursor-pointer ${selectedEvents.has(event.id) ? "bg-primary-50" : ""}`}
                    onClick={() => {
                      // R7 (2026-05-25): pass the current list URL as
                      // backTo so EventDetail's back button restores
                      // the operator's filters + page. URL-encoded to
                      // survive nested ?-separators.
                      const backTo = encodeURIComponent(
                        location.pathname + location.search,
                      );
                      navigate(`/events/${event.id}?backTo=${backTo}`);
                    }}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      {canModify(event.id) && (
                        <input
                          type="checkbox"
                          checked={selectedEvents.has(event.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleEventSelection(event.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <Link
                            to={`/events/${event.id}?backTo=${encodeURIComponent(
                              location.pathname + location.search,
                            )}`}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline"
                          >
                            {event.title}
                          </Link>
                          {event.is_recurring && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              Recurring
                            </span>
                          )}
                          {event.cta_count > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                              title={`${event.cta_count} Call to Action${event.cta_count !== 1 ? "s" : ""}`}
                            >
                              {event.cta_count} CTA
                              {event.cta_count !== 1 ? "s" : ""}
                            </span>
                          )}
                          {event.comment_count > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                              title={`${event.comment_count} Comment${event.comment_count !== 1 ? "s" : ""}`}
                            >
                              {event.comment_count}{" "}
                              <ChatBubbleLeftRightIcon className="ml-1 h-3 w-3" />
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {event.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                        {event.type || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        {formatEventDateRange(
                          event.start_time,
                          event.end_time,
                          displayTz || event.timezone,
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        {event.all_day
                          ? "All day"
                          : formatEventTimeWithZone(
                              event.start_time,
                              event.timezone,
                              displayTz,
                            )}
                      </div>
                      <EventDailyHoursList
                        days={resolveDailyHours(
                          event,
                          dayHoursMap?.get(event.id),
                        )}
                        compact
                        className="mt-0.5 text-gray-500"
                        note={
                          displayTz && displayTz !== event.timezone
                            ? "hours each day, event local time"
                            : undefined
                        }
                      />
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      <div className="max-w-xs truncate">
                        {getLocationDisplay(event)}
                      </div>
                    </td>
                    {/* R4 (2026-05-17): State column. Fall through:
                        location_state → location_address.state → state.
                        Display-only fallback; the filter uses the
                        indexed location_state column directly. */}
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {event.location_state ||
                        (typeof event.location_address === "object" &&
                          event.location_address?.state) ||
                        event.state ||
                        ""}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                      {promoCodes?.[event.id] ?? ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <EventStatusBadge event={event} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(event as { not_advertised?: boolean })
                        .not_advertised && (
                        <span
                          className="mr-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700"
                          title={NOT_ADVERTISED_TITLE}
                          data-testid="not-advertised-badge"
                        >
                          Not advertised
                        </span>
                      )}
                      {showsSetupIncomplete(event) ? (
                        <span
                          className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800"
                          title="Setup not finished — saved from the create wizard to complete later."
                        >
                          Incomplete
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Ready</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canModify(event.id) && (
                        <>
                          <button
                            className="text-orange-600 hover:text-orange-900"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancel(event.id, event.title);
                            }}
                            title="Cancel event (change status to cancelled)"
                          >
                            Cancel
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900 ml-3"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(event.id);
                            }}
                            title="Delete event (hide from list)"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — visible only when there's more than one page of
            events. Matches the Organizations pattern: server-side
            Showing X–Y of Z plus Previous/Next. Client-side filters
            (search / type / date range) further reduce the rows
            actually rendered, so the counts here describe the unfiltered
            server window. */}
        {data?.total !== undefined && data.total > PAGE_SIZE && (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-gray-700">
                Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(page * PAGE_SIZE, data.total)} of {data.total} events
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    updateParams({ page: String(page - 1) }, false)
                  }
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    updateParams({ page: String(page + 1) }, false)
                  }
                  disabled={page * PAGE_SIZE >= data.total}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Cancellation Reason Modal */}
      <CancellationReasonModal
        isOpen={!!cancellingEventId}
        onClose={() => setCancellingEventId(null)}
        onConfirm={handleCancellationConfirm}
        isLoading={isCancelling}
        eventTitle={cancellingEventTitle}
      />
    </div>
  );
};

export default Events;
